"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.parseCurrencyToNumber = exports.centsToCurrencyUnits = exports.dollarsToCents = exports.centsToDollars = exports.formatAsRoasString = exports.formatAsCompactCurrencyString = exports.formatAsCurrencyString = void 0;
/**
 * Formats a number as a currency string in USD
 * @param value - The number to format as currency
 * @param options - Optional formatting options for decimal places
 * @returns A formatted currency string (e.g., "$1,234.56")
 */
const formatAsCurrencyString = (value, options) => {
    const defaultOptions = {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    };
    const finalOptions = Object.assign(Object.assign({}, defaultOptions), options);
    return new Intl.NumberFormat('en-US', Object.assign({ style: 'currency', currency: 'USD' }, finalOptions)).format(value);
};
exports.formatAsCurrencyString = formatAsCurrencyString;
/**
 * Formats a number as a compact currency string with K/M suffixes
 * @param value - The number to format
 * @param options - Optional formatting options
 * @returns A formatted compact currency string (e.g., "$1.5K", "$2.4M")
 */
const formatAsCompactCurrencyString = (value, options) => {
    const { fallbackValue = '-', decimalPlaces = 1 } = options || {};
    if (value === null || value === undefined)
        return fallbackValue;
    if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(decimalPlaces)}M`;
    }
    if (value >= 1000) {
        const thousands = value / 1000;
        const rounded = parseFloat(thousands.toFixed(decimalPlaces));
        // If rounding pushes us to 1000K or higher, convert to millions
        if (rounded >= 1000) {
            return `$${(value / 1000000).toFixed(decimalPlaces)}M`;
        }
        return `$${thousands.toFixed(decimalPlaces)}K`;
    }
    return `$${value}`;
};
exports.formatAsCompactCurrencyString = formatAsCompactCurrencyString;
/**
 * Formats a number as a ROAS string (e.g., "2.45x")
 * @param value - The ROAS value to format
 * @param options - Optional formatting options
 * @returns A formatted ROAS string (e.g., "2.45x")
 */
const formatAsRoasString = (value, options) => {
    const { fallbackValue = '-', decimalPlaces = 2 } = options || {};
    if (value === null || value === undefined)
        return fallbackValue;
    return `${value.toFixed(decimalPlaces)}x`;
};
exports.formatAsRoasString = formatAsRoasString;
/**
 * Converts cents (number) to a dollar display string
 * @param cents - Amount in cents (e.g., 5000)
 * @returns Dollar string with 2 decimal places (e.g., "50.00"), or empty string if null/undefined
 */
const centsToDollars = (cents) => {
    if (cents === null || cents === undefined)
        return '';
    return (cents / 100).toFixed(2);
};
exports.centsToDollars = centsToDollars;
/**
 * Converts a dollar string to cents (number)
 * @param dollars - Dollar amount as string (e.g., "50.00" or "50")
 * @returns Amount in cents (e.g., 5000), or null if empty/invalid
 */
const dollarsToCents = (dollars) => {
    if (!dollars || dollars.trim() === '')
        return null;
    const dollarsNum = parseFloat(dollars);
    if (isNaN(dollarsNum))
        return null;
    return Math.round(dollarsNum * 100);
};
exports.dollarsToCents = dollarsToCents;
/** Converts storefront integer cents to currency units; invalid input becomes 0 so $0 still sends. */
const centsToCurrencyUnits = (cents) => {
    const n = Number(cents);
    if (!Number.isFinite(n)) {
        return 0;
    }
    return Math.round(n) / 100;
};
exports.centsToCurrencyUnits = centsToCurrencyUnits;
const parseCurrencyToNumber = (value) => {
    if (!value || typeof value !== 'string') {
        throw new Error('Input must be a non-empty string');
    }
    // Remove currency symbol, comma, and whitespace
    const cleanValue = value.replace(/[^-.\d]/g, '');
    if (!cleanValue) {
        throw new Error('Invalid currency format: no numeric value found');
    }
    const number = parseFloat(cleanValue);
    if (isNaN(number) || !isFinite(number)) {
        throw new Error('Invalid currency format: could not parse to number');
    }
    return number;
};
exports.parseCurrencyToNumber = parseCurrencyToNumber;
